/**
 * (C) Short clips (owner, Sep 25: "tiktok like app where you could watch short clips (generated in admin media tab) … some as
 * talking head style, some with video and music, some with video and someone commenting on the video") — docs/DESIGN_CLIPS.md.
 *
 * A clip is a `video` media entry with **tracks**: the shots (`prompt`, then `shots`), a voice (`line`, spoken by the entry's
 * `character`), a talking head (the `layout`), the music (`music`: a song from the Music tab or a prompt) and one-shot
 * sounds (`sfx`). Every piece that has to be rendered is a **part** with its own takes on the Media tab: the stem
 * `<id>-<part>` (`clip_x-voice`, `clip_x-shot2`, `clip_x-head`, `clip_x-music`, `clip_x-sfx1`) is the take's file name and
 * the key in the public index, the first shot keeps the entry's own id. The pieces are mixed where they play (the phone,
 * later the TV): a video element or two and a few audio elements on one clock — no muxing on the server, no ffmpeg.
 *
 * Pure: the server (server/media.ts) takes the parts from here, the phone's Tock app (src/game/tock.ts) the plan and the feed.
 */
import type { ClipLayout, Media } from '../content/types';
import { hash, hashStr } from '../world/rng';

export type PartKind = 'shot' | 'voice' | 'head' | 'music' | 'sfx';
/** a renderable piece of a clip: `part` '' is the first shot, which keeps the entry's own id as its stem */
export interface ClipPart { part: string; stem: string; kind: PartKind; /** the shot's or the sound's place in its list */ index: number }

/** a shot's length when it names none: Wan 2.2 5B's comfortable five seconds (81 frames at 16 fps) */
export const SHOT_SECONDS = 5;
/** a talking head is as long as its voice take plus this beat (the calls' rule, server/media.ts) */
export const HEAD_TAIL = 0.6;

export const partStem = (id: string, part: string): string => (part ? `${id}-${part}` : id);
/** `clip_x-voice` → { id: 'clip_x', part: 'voice' } */
export function splitStem(stem: string): { id: string; part: string } { const i = stem.indexOf('-'); return i < 0 ? { id: stem, part: '' } : { id: stem.slice(0, i), part: stem.slice(i + 1) }; }

export function layoutOf(m: Media): ClipLayout { return m.layout ?? (m.prompt ? 'video' : 'head'); }
/** the layouts with a talking head in them */
export const hasHead = (l: ClipLayout): boolean => l !== 'video';
/** a video with tracks (or on the feed) is a clip of parts; a plain video (the news clip in Messages) stays one render */
export function isClip(m: Media): boolean { return m.kind === 'video' && (m.show === 'feed' || !!(m.layout || m.shots?.length || m.music || m.sfx?.length || m.line)); }

/** the parts a clip needs rendered, in the order a batch renders them: the voice first (a head is built on it), the shots, the head, the music, the sounds */
export function clipParts(m: Media): ClipPart[] {
  if (!isClip(m)) return [];
  const out: ClipPart[] = []; const at = (part: string, kind: PartKind, index = 0) => out.push({ part, stem: partStem(m.id, part), kind, index });
  const lay = layoutOf(m);
  if (m.line?.trim()) at('voice', 'voice');
  if (m.prompt && lay !== 'head') at('', 'shot', 0);
  if (lay !== 'head') (m.shots ?? []).forEach((_, i) => at(`shot${i + 2}`, 'shot', i + 1));
  if (hasHead(lay) && m.line?.trim()) at('head', 'head');
  if (m.music?.prompt && !m.music.song) at('music', 'music');
  (m.sfx ?? []).forEach((s, i) => { if (s.prompt && !s.id) at(`sfx${i + 1}`, 'sfx', i); });
  return out;
}
/** a shot's render length: a clip's shots are short (they loop to fill the clip); a plain video is as long as it says */
export function shotSeconds(m: Media, index: number): number {
  if (!isClip(m)) return m.seconds ?? SHOT_SECONDS;
  if (index === 0) return Math.min(m.seconds ?? SHOT_SECONDS, SHOT_SECONDS);
  return m.shots?.[index - 1]?.seconds ?? SHOT_SECONDS;
}
/** the talking head's frame: the whole screen, a bubble in the corner, or the lower half */
export function headSize(l: ClipLayout): [number, number] { return l === 'react' ? [384, 384] : l === 'split' ? [480, 416] : [480, 832]; }
/** the line as it is heard — the Chatterbox tags ([laugh], [sigh]) are not words */
export const spoken = (line: string | undefined): string => (line ?? '').replace(/\[[^\]]*\]/g, ' ').replace(/\s+/g, ' ').trim();
/** a guess at how long a line takes to say, when no take tells (about 2.7 words a second, and a breath) */
export const sayingSeconds = (line: string | undefined): number => { const n = spoken(line).split(' ').filter(Boolean).length; return n ? n / 2.7 + 0.4 : 0; };

// ---- the plan: what plays where and when ----
export interface ClipTrack { src: string; at: number; gain: number; /** seconds into the file */ from?: number }
export interface ClipPlan {
  id: string; layout: ClipLayout; total: number;
  /** the shots' files in order (they loop) */
  shots: string[];
  /** the talking head (it carries the voice), its length, and for `cut` when the shots take over */
  head?: string; headSeconds?: number; cutAt?: number;
  /** the voice-over when there is no head to carry it */
  voice?: ClipTrack;
  music?: ClipTrack & { song?: string };
  sfx: { id?: string; src?: string; at: number; gain: number }[];
  /** the auto-captions, a few words at a time over the voice */
  captions: { text: string; at: number; until: number }[];
  /** every part and song it needs is rendered */
  ready: boolean; missing: string[];
}
export interface PlanSources {
  /** the rendered file of a stem (the public index's pick), with the length its take recorded */
  part(stem: string): { url: string; seconds?: number } | undefined;
  /** a song's rendered file (the Music tab's) */
  song?(id: string): string | undefined;
}
export function clipPlan(m: Media, src: PlanSources): ClipPlan {
  const lay = layoutOf(m); const missing: string[] = [];
  const got = (stem: string) => { const p = src.part(stem); if (!p) missing.push(stem); return p; };
  for (const p of clipParts(m)) got(p.stem);
  const url = (stem: string) => src.part(stem)?.url;
  const shots = lay === 'head' ? [] : [m.prompt ? url(m.id) : undefined, ...(m.shots ?? []).map((_, i) => url(partStem(m.id, `shot${i + 2}`)))].filter((x): x is string => !!x);
  const voiceTake = m.line ? src.part(partStem(m.id, 'voice')) : undefined;
  const said = voiceTake?.seconds ?? sayingSeconds(m.line);
  const headTake = hasHead(lay) && m.line ? src.part(partStem(m.id, 'head')) : undefined;
  const headSeconds = hasHead(lay) && m.line ? (headTake?.seconds ?? said + HEAD_TAIL) : undefined;
  const shotLen = lay === 'head' ? 0 : [m.prompt ? shotSeconds(m, 0) : 0, ...(m.shots ?? []).map((_, i) => shotSeconds(m, i + 1))].reduce((a, b) => a + b, 0);
  const lineAt = hasHead(lay) ? 0 : (m.lineAt ?? 0.3);
  const total = Math.max(1, m.seconds ?? Math.max(shotLen, (headSeconds ?? 0) + (lay === 'cut' ? shotLen : 0), m.line ? lineAt + said : 0, SHOT_SECONDS));
  const plan: ClipPlan = { id: m.id, layout: lay, total, shots, sfx: [], captions: [], ready: false, missing };
  if (headSeconds !== undefined) { plan.head = headTake?.url; plan.headSeconds = headSeconds; if (lay === 'cut') plan.cutAt = Math.min(total - 1, headSeconds); }
  const talking = !!m.line?.trim();
  if (talking && !hasHead(lay) && voiceTake) plan.voice = { src: voiceTake.url, at: lineAt, gain: 1 };
  if (m.music) {
    const gain = m.music.gain ?? (talking ? 0.35 : 0.8);
    if (m.music.song) { const s = src.song?.(m.music.song); if (!s) missing.push(`song:${m.music.song}`); else plan.music = { src: s, at: m.music.at ?? 0, gain, from: m.music.from ?? 0, song: m.music.song }; }
    else { const p = url(partStem(m.id, 'music')); if (p) plan.music = { src: p, at: m.music.at ?? 0, gain, from: m.music.from ?? 0 }; }
  }
  (m.sfx ?? []).forEach((s, i) => { const gain = s.gain ?? 0.8; if (s.id) plan.sfx.push({ id: s.id, at: s.at, gain }); else if (s.prompt) { const p = url(partStem(m.id, `sfx${i + 1}`)); if (p) plan.sfx.push({ src: p, at: s.at, gain }); } });
  if (talking) plan.captions = captionsOf(m.line!, lineAt, said);
  plan.ready = missing.length === 0 && (shots.length > 0 || !!plan.head);
  return plan;
}
/** the line cut into short bursts spread over the time it takes to say — the feed's burned-in captions */
export function captionsOf(line: string, at: number, seconds: number): { text: string; at: number; until: number }[] {
  const words = spoken(line).split(' ').filter(Boolean); if (!words.length) return [];
  const chunks: string[] = []; let cur: string[] = [];
  for (const w of words) { cur.push(w); if (cur.length >= 4 || /[.!?,;:]$/.test(w) && cur.length >= 2) { chunks.push(cur.join(' ')); cur = []; } }
  if (cur.length) chunks.push(cur.join(' '));
  const per = Math.max(0.4, seconds) / words.length; let t = at; const out: { text: string; at: number; until: number }[] = [];
  for (const c of chunks) { const d = c.split(' ').length * per; out.push({ text: c, at: t, until: t + d }); t += d; }
  return out;
}

// ---- the feed ----
/** the feed's clips: the `show: feed` entries (the plans say which are ready) */
export const feedEntries = (all: Media[]): Media[] => all.filter((m) => m.kind === 'video' && m.show === 'feed').sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
/** the order a player scrolls them in: a shuffle seeded by who they are, the same every time they open the app */
export function feedOrder(ids: string[], who: string): string[] {
  const out = [...ids].sort(); const seed = hashStr(`tock:${who}`);
  for (let i = out.length - 1; i > 0; i--) { const j = hash(seed, i) % (i + 1); [out[i], out[j]] = [out[j], out[i]]; }
  return out;
}
/** the next / previous clip: the feed wraps round, as an endless scroll does */
export const stepFeed = (i: number, n: number, d: number): number => (n ? (((i + d) % n) + n) % n : 0);
/** the numbers under a clip — views, likes, comments, shares — made up once per clip, the same for everyone */
export function clipStats(id: string): { views: number; likes: number; comments: number; shares: number } {
  const h = hashStr(`stats:${id}`); const r = (k: number) => hash(h, k) / 4294967296;
  const views = Math.round(800 * Math.pow(10, r(1) * 3.6)); // (from 800 to about three million)
  const likes = Math.round(views * (0.03 + r(2) * 0.12)), comments = Math.round(likes * (0.01 + r(3) * 0.06)), shares = Math.round(likes * (0.005 + r(4) * 0.05));
  return { views, likes, comments, shares };
}
/** 1234 → "1.2K", 3400000 → "3.4M" */
export function fmtCount(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1e6) return `${(n / 1000).toFixed(n < 1e4 ? 1 : 0).replace(/\.0$/, '')}K`;
  return `${(n / 1e6).toFixed(n < 1e7 ? 1 : 0).replace(/\.0$/, '')}M`;
}
/** the creator's handle: the entry's own, else made from the character's name or `from` */
export function handleOf(m: Media, characterName?: string): string {
  if (m.handle) return m.handle;
  const base = (characterName ?? m.from ?? 'someone').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 24);
  return `@${base || 'someone'}`;
}
